import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AppThunk } from '../../store';
import axios from 'axios';
import * as fromAuth from './../../auth/state';
import { RecursivePartial } from '../../../interfaces/recursive-partial';
import get from 'lodash.get';
import { LabResult } from '../../../interfaces/lab-result';
import { getAccessToken } from './../../auth/state';

const BASE_API = process.env['REACT_APP_HUM_ADMIN_API_URL'] ?? ' ';
const instance = axios.create({
  baseURL: BASE_API,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  },
});

interface Paging {
  page: number;
  pageSize: number;
  startKey?: string;
  sortType?: 'asc' | 'desc';
  sortField?: string;
  search?: string;
}

interface LabResultsState {
  list: LabResult[];
  paginationKeys: string[];
  isNextPage: boolean;
  isPrevPage: boolean;
  paging: Paging;
  loading: boolean;
  listError: string | null;
  current: LabResult | null;
  currentError: string | null;
}

const initialState: LabResultsState = {
  list: [],
  paginationKeys: [],
  isNextPage: false,
  isPrevPage: false,
  paging: {
    page: 0,
    pageSize: 30,
    sortType: 'desc',
    sortField: 'sampleTakenDate',
    startKey: '',
  },
  loading: false,
  listError: null,
  current: null,
  currentError: null,
};

export const labResultsSlice = createSlice({
  name: 'labResults',
  initialState,
  reducers: {
    startLoading: (state, action: PayloadAction<Paging | undefined>) => {
      if (action.payload) {
        const { page: currentPage } = state.paging;
        const { page: newPage } = action.payload;
        const isNextPage = newPage > currentPage;
        const isPrevPage = newPage < currentPage;
        state.paging = action.payload;
        state.isNextPage = isNextPage;
        state.isPrevPage = isPrevPage;
        if (newPage === 0) {
          state.paginationKeys = [];
        }
        if (isPrevPage) {
          state.paginationKeys.pop();
        }
      }
      state.loading = true;
    },
    loadLabResultsSuccess: (state, action: PayloadAction<LabResult[]>) => {
      state.loading = false;
      state.list = action.payload;
      const lastItem = action.payload[action.payload.length - 1];
      const nextPageAvailable = action.payload.length === state.paging.pageSize;
      if (
        lastItem &&
        (state.isNextPage || state.paging.page === 0 || (!state.isNextPage && !state.isPrevPage)) &&
        nextPageAvailable
      ) {
        state.paginationKeys.push(lastItem.id);
      }
      state.listError = '';
    },
    loadLabResultsError: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.listError = action.payload;
    },
    clearCurrent: state => {
      state.current = null;
      state.currentError = '';
      const isLastPage = state.list.length < state.paging.pageSize;
      if (!isLastPage) {
        state.paginationKeys.pop();
      }
    },
    loadLabResultByIdSuccess: (state, action: PayloadAction<LabResult>) => {
      state.loading = false;
      state.current = action.payload;
      state.currentError = '';
    },
    loadLabResultByIdError: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.currentError = action.payload;
    },
    updateLabResultByIdSuccess: (state, action: PayloadAction<LabResult>) => {
      state.loading = false;
      state.current = action.payload;
      state.currentError = '';
    },
    updateLabResultByIdError: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.currentError = action.payload;
    },
  },
  extraReducers: {
    [fromAuth.logoutSuccess.type]: state => (state = initialState),
  },
});

export const {
  startLoading,
  loadLabResultsSuccess,
  loadLabResultsError,
  clearCurrent,
  loadLabResultByIdSuccess,
  loadLabResultByIdError,
  updateLabResultByIdSuccess,
  updateLabResultByIdError,
} = labResultsSlice.actions;

export const loadLabResults =
  (paging: Paging): AppThunk =>
  async (dispatch, getState) => {
    const currentPaging = getState().labResults.paging;
    const pageSizeChanged = paging.pageSize !== currentPaging.pageSize;
    const page = pageSizeChanged ? 0 : paging.page;
    dispatch(startLoading({ ...paging, page }));
    const paginationKeys = getState().labResults.paginationKeys;
    const startKey =
      paginationKeys.length && page > 0 ? paginationKeys[paginationKeys.length - 1] : '';
    try {
      const accessToken = await getAccessToken();
      if (accessToken) {
        const response = await instance.get<LabResult[]>(`lab-results`, {
          params: {
            ...paging,
            startKey,
          },
          headers: {
            Authorization: `Bearer ${accessToken.getJwtToken()}`,
          },
        });
        dispatch(loadLabResultsSuccess(response.data));
        return response.data;
      } else {
        dispatch(loadLabResultsError('Missing token'));
      }
    } catch (e: any) {
      dispatch(loadLabResultsError(get(e, 'response.data.error', e.message)));
    }
  };

export const loadLabResultById =
  (id: string): AppThunk =>
  async dispatch => {
    dispatch(startLoading());
    dispatch(clearCurrent());
    try {
      const accessToken = await getAccessToken();
      if (accessToken) {
        const response = await instance.get<LabResult>(`lab-results/${id}`, {
          headers: {
            Authorization: `Bearer ${accessToken.getJwtToken()}`,
          },
        });
        dispatch(loadLabResultByIdSuccess(response.data));
      } else {
        dispatch(loadLabResultByIdError('Missing token'));
      }
    } catch (e: any) {
      dispatch(loadLabResultByIdError(get(e, 'response.data.error', e.message)));
    }
  };

export const updateLabResultById =
  (id: string, labResult: RecursivePartial<LabResult>): AppThunk =>
  async (dispatch, getState) => {
    dispatch(startLoading());
    try {
      const accessToken = await getAccessToken();
      if (accessToken) {
        const response = await instance.patch<LabResult>(`lab-results/${id}`, labResult, {
          headers: {
            Authorization: `Bearer ${accessToken.getJwtToken()}`,
          },
        });
        dispatch(updateLabResultByIdSuccess(response.data));
      } else {
        dispatch(updateLabResultByIdError('Missing token'));
      }
    } catch (e: any) {
      dispatch(updateLabResultByIdError(get(e, 'response.data.error', e.message)));
    }
  };

export default labResultsSlice.reducer;
